import React from "react";
import type { ValidationResult } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import type { Size } from "../../utils/controlStyles";
import {
  BaseInputProps,
  Description,
  FieldError,
  getInputBaseStyles,
  InputWrapper,
  Label,
  useInputFocus,
} from "../Field";

const getTextAreaStyles = (props: {
  isInvalid?: boolean;
  isDisabled?: boolean;
  isResizable?: boolean;
  isFocused?: boolean;
  transparent?: boolean;
  size?: Size;
  className?: string;
}): string => {
  // Get base input styles
  const baseStyles = getInputBaseStyles({
    isInvalid: props.isInvalid,
    isDisabled: props.isDisabled,
    isFocused: props.isFocused,
    transparent: props.transparent,
    size: props.size,
  });

  // Add TextArea-specific styles
  const textAreaStyles = `min-h-[100px] py-2 ${
    props.isDisabled ? "resize-none" : props.isResizable ? "resize-y" : "resize-none"
  }`;

  return twMerge(baseStyles, textAreaStyles, props.className);
};

/**
 * TextArea
 *
 * Multi-line text input with Edges styling, label, description, and error.
 */
export interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size" | "disabled">,
    BaseInputProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  tooltip?: string;
  size?: Size;
  isInvalid?: boolean;
  isDisabled?: boolean;
  /**
   * Allow user resizing of the textarea. Defaults to false (non-resizable).
   * When true, enables vertical resize.
   */
  isResizable?: boolean;
  validationResult?: ValidationResult;
}

export function TextArea({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  required,
  transparent,
  isInvalid,
  isDisabled,
  isResizable = false,
  className,
  validationResult,
  reserveErrorSpace = true,
  ...props
}: TextAreaProps) {
  const { isFocused, handleFocus, handleBlur } = useInputFocus();

  return (
    <div className="group flex flex-col">
      <div className="flex flex-col">
        {label && (
          <Label size={size} tooltip={tooltip} isRequired={required}>
            {label}
          </Label>
        )}
        <InputWrapper>
          <textarea
            {...props}
            required={required}
            onFocus={(e) => {
              // Properly handle the textarea focus event
              if (props.onFocus) {
                props.onFocus(e);
              }
              handleFocus(e as unknown as React.FocusEvent<HTMLInputElement>);
            }}
            onBlur={(e) => {
              // Properly handle the textarea blur event
              if (props.onBlur) {
                props.onBlur(e);
              }
              handleBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
            }}
            className={getTextAreaStyles({
              isInvalid,
              isDisabled,
              isResizable,
              isFocused,
              size,
              transparent,
              className,
            })}
            disabled={isDisabled}
          />
        </InputWrapper>
        {reserveErrorSpace ? (
          <div className="mt-1 min-h-[20px]">
            {typeof errorMessage === "string" ? (
              <FieldError size={size}>{errorMessage}</FieldError>
            ) : typeof errorMessage === "function" && validationResult ? (
              <FieldError size={size}>{errorMessage(validationResult)}</FieldError>
            ) : description ? (
              <Description size={size}>{description}</Description>
            ) : null}
          </div>
        ) : (
          <>
            {typeof errorMessage === "string" ? (
              <FieldError size={size} className="mt-1">
                {errorMessage}
              </FieldError>
            ) : typeof errorMessage === "function" && validationResult ? (
              <FieldError size={size} className="mt-1">
                {errorMessage(validationResult)}
              </FieldError>
            ) : description ? (
              <Description size={size} className="mt-1">
                {description}
              </Description>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
